// ImportTemplateFieldsModal.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './ImportTemplateFieldsModal.css';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import CheckboxList from 'components/checkboxlist/CheckboxList';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';

/**
 * ImportTemplateFieldsModal
 *
 * Lets the user choose which fields to include in a CSV import template.
 * Title is always included; optional schema fields default to all selected.
 *
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Called when the modal is dismissed.
 * @param {function} onConfirm - Called with selected field keys (optional fields only).
 * @param {string} titleFieldLabel - Locked title column label.
 * @param {Array<{ key: string, label: string }>} fields - Optional importable fields.
 *
 * @returns {ReactNode}
 */
const ImportTemplateFieldsModal = React.memo(({
  isOpen,
  onClose,
  onConfirm,
  titleFieldLabel = 'Title',
  fields = [],
}) => {

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- STATE ----- //

  const [selectedKeys, setSelectedKeys] = useState([]);

  // ----- MEMOS ----- //

  const fieldOptions = useMemo(() => (
    (Array.isArray(fields) ? fields : [])
      .map((field) => {
        const key = String(field?.key || '').trim();
        if (!key) return null;
        return {
          value: key,
          label: String(field?.label || key).trim() || key,
        };
      })
      .filter(Boolean)
  ), [fields]);

  const allFieldKeys = useMemo(
    () => fieldOptions.map((option) => option.value),
    [fieldOptions],
  );

  const allSelected = allFieldKeys.length > 0
    && allFieldKeys.every((key) => selectedKeys.includes(key));

  // ----- EFFECTS ----- //

  // Reset selection to all fields whenever the picker opens.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedKeys(allFieldKeys);
  }, [isOpen, allFieldKeys]);

  // ----- HANDLERS ----- //

  /**
   * Toggles select-all for optional fields.
   */
  const handleToggleAll = () => {
    setSelectedKeys(allSelected ? [] : allFieldKeys);
  };

  /**
   * Confirms the current field selection.
   */
  const handleConfirm = () => {
    if (typeof onConfirm !== 'function') return;
    onConfirm(selectedKeys);
  };

  if (!isOpen) return null;

  // ----- RENDER ----- //

  return (
    <Modal
      iconKey="download"
      title="Choose template fields"
      isOpen={isOpen}
      width={isMobile ? '100%' : 420}
      height="auto"
      maxHeight="90vh"
      onClose={onClose}
    >
      <ModalForm gap="12px" containScroll>
        <ModalHint hint="The title column is always included. Uncheck any other fields you do not need." />

        {/* REQUIRED TITLE */}
        <div className="import-template-fields-modal-required">
          <Checkbox
            value={true}
            label={`${titleFieldLabel} (required)`}
            size={20}
            fontSize="13px"
            onChange={() => {}}
          />
        </div>

        {fieldOptions.length > 0 ? (
          <>
            {/* ALL TOGGLE */}
            <div className="import-template-fields-modal-all">
              <Checkbox
                value={allSelected}
                label="All"
                size={20}
                fontSize="13px"
                fontWeight={600}
                onChange={handleToggleAll}
              />
            </div>

            {/* FIELD LIST */}
            <div className="import-template-fields-modal-list">
              <CheckboxList
                options={fieldOptions}
                values={selectedKeys}
                multiSelect
                checkboxSize={20}
                fontSize="13px"
                onChange={(nextValues) => setSelectedKeys(nextValues)}
              />
            </div>
          </>
        ) : (
          <ModalHint hint="This type has no additional importable fields." />
        )}
      </ModalForm>

      <ModalButtons>
        <ModalButtonSection align="END">
          <Button
            iconKey="close"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            iconKey="download"
            label="Download Template"
            onClick={handleConfirm}
          />
        </ModalButtonSection>
      </ModalButtons>
    </Modal>
  );
});

// Dev Tools
ImportTemplateFieldsModal.displayName = 'ImportTemplateFieldsModal';

export default ImportTemplateFieldsModal;
